import { Key, useCallback } from 'react'

import { useTreeMutations } from '../components/TrackChangesPanel/hooks/TreeMutations/useTreeMutations'
import { DataNode, PartType } from '../types/types'
import * as utils from '../utils'
import {
    useActiveTab,
    useExpandedKeys,
    useWorkspace2Actions,
} from '../Workspace2Store'
import { useArticleActions } from './useArticleActions'
import { useParagraphActions } from './useParagraphActions'

/** Interface for the useEditorActions hook props. */
interface UseEditorActionsProp {
    /** The current tree data structure */
    treeData: DataNode[]
    /** Function to fetch product family data */
    fetchProductFamily?: (
        partType: PartType,
        rootArticleContentItemIds?: string[]
    ) => void
    /** Utility function to get keys of nodes to collapse */
    withKeysToCollapse: (treeNodes: DataNode[]) => Key[]
    /** Utility function to get keys of nodes to fetch */
    withKeysToFetch: (keysToExpand: string[]) => string[]
    /** The array of Article content item IDs on the Tree */
    memoArticleContentItemIds: string[]
    /** The array of content item IDs on the Tree */
    memoContentItemViewIds: string[]
    /** Flag that indicates whether to show shared property label */
    showSharedPropertyLabel?: boolean
}

/**
 * Custom hook to manage actions in the Workspace Editor.
 * @param {UseEditorActionsProp} UseEditorActionsProp - The hook props.
 * @returns {object} The list of handler functions for the Editor.
 */
export const useEditorActions = ({
    treeData,
    fetchProductFamily,
    withKeysToCollapse,
    withKeysToFetch,
    memoContentItemViewIds,
    showSharedPropertyLabel = false,
}: UseEditorActionsProp) => {
    const activeTab = useActiveTab()
    const expandedKeys = useExpandedKeys()
    const { setExpandedKeys, setArticlesLoading } = useWorkspace2Actions()

    const { addArticleAfterSibling } = useArticleActions()
    const { addParagraphAfterSibling } = useParagraphActions()
    const { reRenderTree } = useTreeMutations()

    const handleEditorTabChange = useCallback(
        (tabIndex: number) => {
            reRenderTree({
                updatedContentItemViewNodes: [],
                tabIndex,
            })
            if (fetchProductFamily && !showSharedPropertyLabel) {
                fetchProductFamily(utils.getActiveTabLabel(tabIndex + 1))
            }
        },
        [reRenderTree, fetchProductFamily, showSharedPropertyLabel, activeTab]
    )

    const handleExpandAll = useCallback(() => {
        // get all keys that are not expanded yet
        const keysToExpand = memoContentItemViewIds.filter(
            (key) => !expandedKeys.includes(key)
        )
        // find article keys to fetch
        const articleKeysToFetch = withKeysToFetch(keysToExpand)
        // fetch the articles if any
        if (articleKeysToFetch.length > 0) {
            setArticlesLoading(articleKeysToFetch) // to indicate loading
            if (fetchProductFamily) {
                fetchProductFamily(
                    utils.getActiveTabLabel(activeTab),
                    articleKeysToFetch
                )
            }
        }
        // expand all content items
        setExpandedKeys([...expandedKeys, ...keysToExpand])
    }, [memoContentItemViewIds, expandedKeys, fetchProductFamily])

    const handleCollapseAll = useCallback(() => {
        setExpandedKeys(withKeysToCollapse(treeData))
        setArticlesLoading([])
    }, [treeData])

    const addTreeNode = useCallback(
        (sibling: DataNode) => {
            switch (sibling.type) {
                case 'Article':
                    addArticleAfterSibling(sibling)
                    break
                case 'Paragraph':
                    addParagraphAfterSibling(sibling)
                    break
                default:
                    throw new Error(
                        'Unable to add tree node. Unsupported NodeType'
                    )
            }
        },
        [addArticleAfterSibling, addParagraphAfterSibling]
    )

    return {
        handleEditorTabChange,
        handleExpandAll,
        handleCollapseAll,
        addTreeNode,
    }
}
